import React, { useState, useRef, useEffect } from 'react';
import { Button, Col, Label, Row, UncontrolledTooltip } from 'reactstrap';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import { DataGrid } from '@mui/x-data-grid';

import EmailInputModal from '../common/EmailInputModal';
import PhoneInputModal from '../common/PhoneInputModal';
import AddressInputModal from '../common/AddressInputModal';

const ContactInfo = (props) => {
    const [deleteEmailConfirmationState, setDeleteEmailConfirmationState] = useState({
        display: false,
        toDelete: [],
    });
    const [deletePhoneConfirmationState, setDeletePhoneConfirmationState] = useState({
        display: false,
        toDelete: [],
    });
    const [deleteAddressConfirmationState, setDeleteAddressConfirmationState] = useState({
        display: false,
        toDelete: [],
    });
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [disableAddressDelete, setDisableAddressDelete] = useState(false);
    const [disableEmailDelete, setDisableEmailDelete] = useState(false);
    const [disablePhoneNumberDelete, setDisablePhoneNumberDelete] = useState(false);

    const [personEmail, setPersonEmail] = useState(null);
    const [personPhone, setPersonPhone] = useState(null);
    const [personAddress, setPersonAddress] = useState(null);

    const [editingPersonEmail, setEditingPersonEmail] = useState(null);

    const handleEmailAddNewClick = () => {
        setEditingPersonEmail({
            personId: props.person.personId,
            isActive: true,
            email: { isActive: true },
        });
        setShowEmailModal(true);
    };

    const handleEmailSelect = (e) => {
        setEditingPersonEmail({ ...e.row });
        setShowEmailModal(true);
    };

    const handlePhoneSelect = (e) => {
        setPersonPhone({ ...e.row });
        setShowPhoneModal(true);
    };

    const handleToggleShowPhoneModal = () => {
        setShowPhoneModal((prev) => !prev);
    };

    const handleToggleShowEmailModal = () => {
        setShowEmailModal((prev) => !prev);
    };

    const handleToggleShowAddressModal = () => {
        setShowAddressModal((prev) => !prev);
    };

    const handlePhoneAddNewClick = () => {
        setPersonPhone({
            personId: props.person.personId,
            isActive: true,
            phone: { isActive: true },
            phoneTypeId: props.phoneTypes[0].lookupId.toString(),
        });
        setShowPhoneModal(true);
    };

    const handleAddressAddNewClick = () => {
        setPersonAddress({
            personId: props.person.personId,
            isActive: true,
            addressTypeId: props.addressTypes[0].lookupId,
            address: {
                isActive: true,
                city: "",
                line1: "",
                line2: "",
                stateId: "",
                zipCode: ""
            },
        });
        setShowAddressModal(true);
    };

    const handleDeletePhoneClick = () => {
        const selectedPhoneIds = deletePhoneConfirmationState.toDelete.map((x) => x.personPhoneId);
        props.onDeletePersonPhone(props.person.personId, selectedPhoneIds);
        setDisablePhoneNumberDelete(true);
    };

    const handleDeleteEmailClick = () => {
        const selectedEmailIds = deleteEmailConfirmationState.toDelete.map((x) => x.personEmailId);
        props.onDeletePersonEmails(props.person.personId, selectedEmailIds);
        setDisableEmailDelete(true);
    };

    const handleAddresDeleteClick = () => {
        const selectedAddressIds = deleteAddressConfirmationState.toDelete.map((x) => x.personAddressId);
        props.onDeletePersonAddresses(props.person.personId, selectedAddressIds);
        setDisableAddressDelete(true);
    };

    const handlePhoneTypeChange = (id) => {
        setPersonPhone({
            ...personPhone,
            phoneTypeId: id,
        })
    };

    const handlePhoneNumberOnChange = (newPersonPhone) => {
        setPersonPhone(newPersonPhone);
    };

    const handleOnIsPrimaryPhoneChanged = (value) => {
        setPersonPhone({
            ...personPhone,
            isPrimary: value
        });
    };

    const handlePhoneInputModalOnSave = () => {
        props.onSavePersonPhone(personPhone);
        setDisablePhoneNumberDelete(false);
    };

    const handleEmailInputModalOnSave = () => {
        props.onSavePersonEmail(editingPersonEmail);
        setDisableEmailDelete(false);
    };

    const handleAddressSelect = (e) => {
        setPersonAddress({ ...e.row });
        setShowAddressModal(true)
    }

    const handleAddressInputModalOnChange = (newPersonAddress) => {
        setPersonAddress({ ...newPersonAddress });
    };

    const handleAddressInputModalOnSave = () => {
        const { addressType, ...addressData } = personAddress;
        props.onSavePersonAddress(addressData);
        setDisableAddressDelete(false);
    };

    const handleEmailSelectionChanged = (e) => {
        if (e.length > 0) {
            setDeleteEmailConfirmationState({
                display: false,
                toDelete: props.emails.filter(x => e.find(y => y === x.personEmailId)),
            });
            setDisableEmailDelete(true);
        }
        else
            setDisableEmailDelete(false);
    }

    const handlePhoneSelectionChanged = (e) => {
        if (e.length > 0) {
            setDeletePhoneConfirmationState({
                display: false,
                toDelete: props.phones.filter(x => e.find(y => y === x.personPhoneId)),
            });
            setDisablePhoneNumberDelete(true);
        }
        else
            setDisablePhoneNumberDelete(false);
    }

    const handleAddressSelectionChanged = (e) => {
        if (e.length > 0) {
            setDeleteAddressConfirmationState({
                display: false,
                toDelete: props.addresses.filter(x => e.find(y => y === x.personAddressId)),
            });
            setDisableAddressDelete(true);
        }
        else
            setDisableAddressDelete(false);
    }

    const handleEmailInputModalOnChange = (newPersonEmail) => {
        setEditingPersonEmail(newPersonEmail); // Make sure you are updating the correct state variable
    };

    return (
        <React.Fragment>
            {editingPersonEmail && (
                <EmailInputModal
                    isOpen={showEmailModal}
                    onToggle={handleToggleShowEmailModal}
                    onSave={handleEmailInputModalOnSave}
                    onChange={handleEmailInputModalOnChange}
                    personEmail={editingPersonEmail}
                />
            )}
            {personPhone && (
                <PhoneInputModal
                    setField={props.setField}
                    isOpen={showPhoneModal}
                    onToggle={handleToggleShowPhoneModal}
                    onSave={handlePhoneInputModalOnSave}
                    onPhoneTypeChange={handlePhoneTypeChange}
                    onNumberChange={handlePhoneNumberOnChange}
                    personPhone={personPhone}
                    phoneTypes={props.phoneTypes}
                    handleOnIsPrimaryPhoneChanged={handleOnIsPrimaryPhoneChanged}
                />
            )}
            {personAddress && (
                <AddressInputModal
                    isOpen={showAddressModal}
                    onToggle={handleToggleShowAddressModal}
                    onSave={handleAddressInputModalOnSave}
                    onChange={handleAddressInputModalOnChange}
                    personAddress={personAddress}
                    addressTypes={props.addressTypes}
                    states={props.states}
                />
            )}
            <Row className='mb-1'>
                <Col>
                    <Label>Email:</Label>
                </Col>
                <Col className='d-flex flex-row-reverse'>
                    <Button id='deleteEmailButton' disabled={!disableEmailDelete} onClick={handleDeleteEmailClick}>
                        <DeleteIcon />
                    </Button>
                    <Button id='addEmailButton' onClick={handleEmailAddNewClick} className='me-1'>
                        <AddIcon />
                    </Button>
                    <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target='addEmailButton'>
                        Add an Email.
                    </UncontrolledTooltip>
                    <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target='deleteEmailButton'>
                        Delete an Email.
                    </UncontrolledTooltip>
                </Col>
            </Row>
            <Row className='mb-5'>
                <Col>
                    <div style={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={props.emails.map(x => { return { id: x.personEmailId, ...x } })}
                            columns={[
                                { field: 'emailAddress', headerName: 'Email Address', flex: 3, },
                                { field: 'isPrimary', headerName: 'Is Primary', flex: 3 }
                            ]}
                            checkboxSelection
                            onRowDoubleClick={handleEmailSelect}
                            onRowSelectionModelChange={handleEmailSelectionChanged}
                            rowHeight = {25}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            sx = {{
                            backgroundColor: 'white'
                            }}
                            getRowClassName={(params) => 'grid-row'} 
                        />
                    </div>
                </Col>
            </Row>
            <Row className='mb-1'>
                <Col>
                    <Label>Phone Numbers:</Label>
                </Col>
                <Col className='d-flex flex-row-reverse'>
                    <Button id='deletePhoneButton' disabled={!disablePhoneNumberDelete} onClick={handleDeletePhoneClick}>
                        <DeleteIcon />
                    </Button>
                    <Button id='addPhoneButton' onClick={handlePhoneAddNewClick} className='me-1'>
                        <AddIcon />
                    </Button>
                    <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target='addPhoneButton'>
                        Add a new phone number.
                    </UncontrolledTooltip>
                    <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target='deletePhoneButton'>
                        Delete a phone number.
                    </UncontrolledTooltip>
                </Col>
            </Row>
            <Row className='mb-5'>
                <Col>
                    <div style={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={props.phones.map(x => { return { id: x.personPhoneId, ...x } })}
                            columns={[
                                {
                                    field: 'name',
                                    valueGetter: (item) => item.row.phoneType?.name || '',
                                    headerName: 'Type',
                                    flex: 3
                                },
                                {
                                    field: 'formattedNumber',
                                    valueGetter: (item) => item.row.phone.formattedNumber,
                                    headerName: 'Number',
                                    flex: 3
                                },
                                { field: 'extension', headerName: 'Extension', flex: 3 },
                                { field: 'isPrimary', headerName: 'Is Primary', flex: 3 }
                            ]}
                            checkboxSelection
                            onRowDoubleClick={handlePhoneSelect}
                            onRowSelectionModelChange={handlePhoneSelectionChanged}
                            rowHeight={25}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            sx={{
                                backgroundColor: 'white'
                            }}
                            getRowClassName={(params) => 'grid-row'} 
                        />
                    </div>
                </Col>
            </Row>
            <Row className='mb-1'>
                <Col>
                    <Label>Addresses:</Label>
                </Col>
                <Col className='d-flex flex-row-reverse'>
                    <Button id='deleteAddressButton' disabled={!disableAddressDelete} onClick={handleAddresDeleteClick}>
                        <DeleteIcon />
                    </Button>
                    <Button id='addAddressButton' onClick={handleAddressAddNewClick} className='me-1'>
                        <AddIcon />
                    </Button>
                    <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target='addAddressButton'>
                        Add a new address.
                    </UncontrolledTooltip>
                    <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target='deleteAddressButton'>
                        Delete an address.
                    </UncontrolledTooltip>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div style={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={props.addresses.map(x => { return { id: x.personAddressId, ...x } })}
                            columns={[
                                {
                                    valueGetter: (item) => item.row.addressType?.name || '',
                                    field: 'name',
                                    headerName: 'Type',
                                    flex: 3
                                },
                                {
                                    valueGetter: (item) => item.row.address.formattedAddress,
                                    field: 'formattedAddress',
                                    headerName: 'Address', 
                                    flex: 3
                                },
                            ]}
                            checkboxSelection
                            onRowSelectionModelChange={handleAddressSelectionChanged}
                            onRowDoubleClick={handleAddressSelect}
                            rowHeight={25}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            sx={{
                                backgroundColor: 'white'
                            }}
                        />
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default ContactInfo;
